import Vue from 'vue'
// import { WebRTCAdaptor } from '@antmedia/webrtc_adaptor'

export function useWebRTC (token, streamName, project, type, streamKey) {
  let vm = new Vue()
  const rating = 5
  let webRTCAdaptor = null
  const streamId = `stream-${streamName}_p-${project}_t-${type}`

  let client = null
  let activeStream = null
  let audioDevice = null
  let videoDevice = null

  // IVS Configuration
  const INGEST_ENDPOINT = '70f88937b05c.global-contribute.live-video.net'
  
  vm.$on('hook:beforeDestroy', async () => {
    if (client) {
      await hangup()
    }
  })

  const initializeClient = async () => {
    client = window.IVSBroadcastClient.create({
      ingestEndpoint: INGEST_ENDPOINT,
      streamConfig: window.IVSBroadcastClient.STANDARD_LANDSCAPE
    })

    return client
  }

  const startBroadcast = async () => {
    if (!client) {
      console.error('Client not initialized')
      return
    }

    try {
      // Start the broadcast
      await client.startBroadcast(streamKey)

      console.log('IVS broadcast started successfully')
    } catch (error) {
      console.error('Error starting IVS broadcast:', error)
      throw error
    }
  }

  // eslint-disable-next-line no-unused-vars
  const call = async (localStream, bitrate = 2500) => {
    try {
      // Initialize IVS client
      await initializeClient()

      // Extract tracks from the MediaStream
      const videoTrack = localStream.getVideoTracks()[0]
      const audioTrack = localStream.getAudioTracks()[0]

      // Add video device if available
      if (videoTrack) {
        videoDevice = await client.addVideoInputDevice(
          new MediaStream([videoTrack]),
          streamId + '_video',
          { index: 0 }
        )
      }

      // // Add audio device if available
      if (audioTrack) {
        audioDevice = await client.addAudioInputDevice(
          new MediaStream([audioTrack]),
          streamId + '_audio'
        )
      }

      // Store the active stream reference
      activeStream = localStream

      // Start broadcasting with delay (matching original behavior)
      setTimeout(() => {
        startBroadcast()
      }, 2000)

    } catch (error) {
      console.error('Error initializing IVS broadcast:', error)
      throw error
    }
  }

  const hangup = async () => {
    if (client) {
      try {
        // Stop the broadcast
        await client.stopBroadcast()

        // Remove devices
        if (videoDevice) {
          client.removeVideoInputDevice(videoDevice)
          videoDevice = null
        }

        if (audioDevice) {
          client.removeAudioInputDevice(audioDevice)
          audioDevice = null
        }

        // Clean up the active stream
        if (activeStream) {
          activeStream.getTracks().forEach(track => track.stop())
          activeStream = null
        }

        console.log('IVS broadcast stopped successfully')
      } catch (error) {
        console.error('Error stopping IVS broadcast:', error)
      }
    }
  }

  const replaceTrack = async (newTrack) => {
    if (!client) {
      console.error('Client not initialized')
      return
    }

    try {
      // Check if we received a stream or track, and get the track
      let videoTrack

      if (newTrack instanceof MediaStream) {
        // If a stream is passed, extract the video track from it
        videoTrack = newTrack.getVideoTracks()[0]
        if (!videoTrack) {
          console.error('No video tracks in the provided stream')
          return
        }
      } else if (newTrack instanceof MediaStreamTrack) {
        // If a track is passed, use it directly
        videoTrack = newTrack
      } else {
        console.error('Invalid data type. Expected MediaStream or MediaStreamTrack')
        return
      }

      // Check if it's a video track
      if (videoTrack.kind !== 'video') {
        console.error('The provided track is not a video track')
        return
      }

      // Remove the old video device
      await client.removeVideoInputDevice(streamId + '_video')

      // Add the new video device
      videoDevice = await client.addVideoInputDevice(
        new MediaStream([videoTrack]),
        streamId + '_video',
        {index: 0}
      )

      // Update the active stream reference
      if (activeStream) {
        const oldVideoTracks = activeStream.getVideoTracks()
        oldVideoTracks.forEach(track => {
          if (track.id !== videoTrack.id) {
            activeStream.removeTrack(track)
            track.stop()
          }
        })

        if (!activeStream.getVideoTracks().includes(videoTrack)) {
          activeStream.addTrack(videoTrack)
        }
      }

      console.log('Video track successfully replaced')
    } catch (error) {
      console.error('Error replacing track:', error)
      throw error
    }
  }

  const replaceTrackByDevice = async (newTrack, type) => {
    if (webRTCAdaptor) {
      if (type === 'video') {
        webRTCAdaptor.updateVideoTrack(newTrack, streamId)
      } else if (type === 'audio') {
        webRTCAdaptor.updateAudioTrack(newTrack, streamId)
      }
    }
  }

  // Optional: Get broadcast statistics
  const getStats = async () => {
    if (client && client.isBroadcasting) {
      try {
        const stats = await client.getWebRTCStats()
        return stats
      } catch (error) {
        console.error('Error getting stats:', error)
        return null
      }
    }
    return null
  }

  // Optional: Check broadcast status
  const isLive = () => {
    return client ? client.isBroadcasting : false
  }

  return {
    call,
    hangup,
    replaceTrack,
    replaceVideoTrack: track => replaceTrackByDevice(track, 'video'),
    replaceAudioTrack: track => replaceTrackByDevice(track, 'audio'),
    getStats,
    isLive,
    rating
  }
}